import { Link } from "react-router-dom";
import { ArrowRight, Users, Waves, Truck, Trash2, Wallet, HeartHandshake, Check, Sprout } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { KineticLines } from "../components/Kinetic";
import { Seo } from "../components/Seo";
import { BUSINESS } from "../data/site";

const TEAM_RATES = [
    { count: "1 team member", rate: "1× base rate" },
    { count: "2 team members", rate: "1.5× base rate" },
    { count: "Each additional team member", rate: "+0.5× base rate" },
];

const GUTTER_FACTORS = [
    "Height of the property",
    "Gutter size",
    "Accessibility",
    "Amount of debris",
    "Overall difficulty of the job",
];

const cardCls = "h-full rounded-3xl border border-forest/10 bg-bone-card p-8";

export default function Pricing() {
    return (
        <main data-testid="pricing-page" className="pt-16">
            <Seo title="Pricing — How We Price Our Quotes | Lawn Busters"
                description="How Lawn Busters quotes: fair hourly labour, per-side gutter pricing, job-by-job junk removal and tipping explained. No surprises — free quotes." />
            <section className="mx-auto max-w-3xl px-6 py-16 text-center lg:py-24">
                <p className="eyebrow text-forest-leaf">Pricing</p>
                <h1 className="mt-4 font-display text-4xl font-bold leading-[1.05] tracking-tight text-ink sm:text-5xl">
                    <KineticLines lines={["How We Price", "Our Quotes"]} />
                </h1>
                <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-moss">
                    At Lawn Busters, we believe in fair, transparent, and flexible pricing. Every property is
                    different, so we consider the size, condition, complexity, accessibility, and time required
                    before providing a quote. We also understand that customers have different budgets, and we'll
                    always do our best to work with you to find a price that suits your needs.
                </p>
                <div className="mt-8 flex flex-wrap justify-center gap-3">
                    <Link to="/estimate" data-testid="pricing-estimate-button"
                        className="rounded-full bg-ochre px-7 py-3.5 text-sm font-bold text-forest-night transition-colors hover:bg-clay hover:text-bone">
                        Get an Instant Estimate <ArrowRight size={14} className="ml-1 inline" />
                    </Link>
                    <Link to="/book-now?type=quote" data-testid="pricing-quote-button"
                        className="rounded-full border border-forest/25 px-7 py-3.5 text-sm font-bold text-forest transition-colors hover:bg-bone-subtle">
                        Get a Free Quote
                    </Link>
                </div>
            </section>

            <section className="mx-auto max-w-6xl px-6 pb-16 lg:pb-24">
                <div className="grid gap-6 md:grid-cols-2">
                    <Reveal>
                        <div data-testid="pricing-labour-rates" className={cardCls}>
                            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-forest text-bone"><Users size={20} /></span>
                            <h2 className="mt-5 font-display text-xl font-bold tracking-tight text-ink sm:text-2xl">Labour Rates</h2>
                            <p className="mt-3 text-sm leading-relaxed text-moss">
                                Our quotes are built on a fair hourly labour rate for the team, with equipment
                                included. Pricing may vary depending on the size and complexity of the job.
                            </p>
                            <p className="mt-4 eyebrow text-forest-leaf">When additional team members are required</p>
                            <div className="mt-3 space-y-2">
                                {TEAM_RATES.map((r) => (
                                    <div key={r.count} className="flex items-center justify-between rounded-xl bg-bone-subtle px-4 py-3">
                                        <span className="text-sm font-semibold text-ink">{r.count}</span>
                                        <span className="font-mono text-xs font-semibold uppercase tracking-wider text-forest-leaf">{r.rate}</span>
                                    </div>
                                ))}
                            </div>
                            <p className="mt-4 text-sm leading-relaxed text-moss">
                                Additional labour may be required for larger, more complex, or time-consuming
                                properties.
                            </p>
                        </div>
                    </Reveal>
                    <Reveal delay={0.08}>
                        <div data-testid="pricing-gutter" className={cardCls}>
                            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-forest text-bone"><Waves size={20} /></span>
                            <h2 className="mt-5 font-display text-xl font-bold tracking-tight text-ink sm:text-2xl">Gutter Cleaning</h2>
                            <p className="mt-3 text-sm leading-relaxed text-moss">
                                Gutter cleaning is generally priced per gutter side.
                            </p>
                            <p className="mt-4 text-sm font-semibold text-ink">The final price depends on:</p>
                            <ul className="mt-3 space-y-2">
                                {GUTTER_FACTORS.map((f) => (
                                    <li key={f} className="flex items-center gap-2.5 text-sm text-moss">
                                        <Check size={14} className="shrink-0 text-forest-leaf" /> {f}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </Reveal>
                    <Reveal delay={0.05} className="md:col-span-2">
                        <div data-testid="pricing-weed" className={cardCls}>
                            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-forest text-bone"><Sprout size={20} /></span>
                            <h2 className="mt-5 font-display text-xl font-bold tracking-tight text-ink sm:text-2xl">Weed Removal</h2>
                            <p className="mt-3 text-sm leading-relaxed text-moss">
                                We offer both chemical weed treatment and manual hand-picking depending on the
                                property and customer's preference.
                            </p>
                            <div className="mt-4 grid gap-4 sm:grid-cols-2">
                                <div className="rounded-xl bg-bone-subtle p-5">
                                    <p className="text-sm font-bold text-ink">Chemical Weed Treatment</p>
                                    <p className="mt-1.5 text-sm leading-relaxed text-moss">
                                        Pricing depends on the size of the property, the amount of weeds, the
                                        treatment required, and associated costs. This charge is added to the final
                                        lawn-care quote.
                                    </p>
                                </div>
                                <div className="rounded-xl bg-bone-subtle p-5">
                                    <p className="text-sm font-bold text-ink">Hand-Picked Weed Removal</p>
                                    <p className="mt-1.5 text-sm leading-relaxed text-moss">
                                        Manual weed removal can require significantly more labour, particularly on
                                        larger properties with heavy weed growth. Smaller residential properties are
                                        generally quicker and easier to hand weed, while larger or heavily overgrown
                                        properties may require additional labour and therefore a higher overall quote.
                                    </p>
                                </div>
                            </div>
                            <p className="mt-4 text-sm leading-relaxed text-moss">
                                We'll assess the property and recommend the most practical option based on the size,
                                condition, and customer's needs.
                            </p>
                        </div>
                    </Reveal>
                    <Reveal delay={0.05}>
                        <div data-testid="pricing-junk" className={cardCls}>
                            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-forest text-bone"><Truck size={20} /></span>
                            <h2 className="mt-5 font-display text-xl font-bold tracking-tight text-ink sm:text-2xl">Junk Removal</h2>
                            <p className="mt-3 text-sm leading-relaxed text-moss">
                                Junk removal is quoted on a job-by-job basis. We consider the amount and size of
                                junk, how difficult it is to access and remove, and the amount of manual labour
                                required.
                            </p>
                            <p className="mt-4 text-sm leading-relaxed text-moss">
                                The final price may therefore vary depending on the size, weight, accessibility, and
                                complexity of the removal.
                            </p>
                        </div>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <div data-testid="pricing-tipping" className={cardCls}>
                            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-forest text-bone"><Trash2 size={20} /></span>
                            <h2 className="mt-5 font-display text-xl font-bold tracking-tight text-ink sm:text-2xl">Tipping Fees</h2>
                            <p className="mt-3 text-sm leading-relaxed text-moss">
                                Tipping and disposal fees are separate from your lawn-care quote.
                            </p>
                            <div className="mt-4 rounded-xl bg-ochre/15 px-4 py-3">
                                <span className="text-sm font-bold text-ink">Tipping / disposal is charged per trailer load</span>
                            </div>
                            <p className="mt-4 text-sm leading-relaxed text-moss">
                                Additional disposal costs may apply depending on the type and amount of waste.
                            </p>
                        </div>
                    </Reveal>
                    <Reveal delay={0.05} className="md:col-span-2">
                        <div data-testid="pricing-deposits" className={`${cardCls} flex flex-col gap-5 sm:flex-row sm:items-start`}>
                            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-ochre text-forest-night"><Wallet size={20} /></span>
                            <div>
                                <h2 className="font-display text-xl font-bold tracking-tight text-ink sm:text-2xl">Deposits</h2>
                                <p className="mt-3 text-sm leading-relaxed text-moss">
                                    For larger jobs, we may require a deposit to secure the booking.
                                </p>
                                <p className="mt-3 text-sm leading-relaxed text-moss">
                                    This is because larger jobs generally require a full day's work, dedicated team
                                    members, equipment, and preparation. The remaining balance is payable according to
                                    the agreed payment terms.
                                </p>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            <section className="bg-forest-night py-16 text-bone lg:py-24" data-testid="pricing-promise">
                <div className="mx-auto max-w-3xl px-6 text-center">
                    <Reveal>
                        <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-ochre text-forest-night"><HeartHandshake size={20} /></span>
                        <h2 className="mt-6 font-display text-2xl font-bold tracking-tight sm:text-3xl">Our Promise</h2>
                        <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-bone/70">
                            We aim to provide clear, upfront quotes with no unnecessary surprises. If you have a
                            budget you're working with, let us know when requesting a quote — we'll do our best to
                            find a practical solution that works for both you and your property.
                        </p>
                        <div className="mt-9 flex flex-wrap justify-center gap-3">
                            <Link to="/book-now?type=quote" data-testid="pricing-quote-button"
                                className="rounded-full bg-ochre px-8 py-3.5 text-sm font-bold text-forest-night transition-colors hover:bg-bone">
                                Get a Free Quote <ArrowRight size={14} className="ml-1 inline" />
                            </Link>
                            <a href={BUSINESS.phoneHref} data-testid="pricing-call-button"
                                className="rounded-full border border-bone/30 px-8 py-3.5 text-sm font-bold text-bone transition-colors hover:bg-bone/10">
                                Call {BUSINESS.phone}
                            </a>
                        </div>
                    </Reveal>
                </div>
            </section>
        </main>
    );
}
